import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import BugReportIcon from '@mui/icons-material/BugReport';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import NewReleasesIcon from '@mui/icons-material/NewReleases';
import StarIcon from '@mui/icons-material/Star';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  Container,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { blue, green, grey, orange } from '@mui/material/colors';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';

import { releaseNotes } from './releaseNotesData';

const getChangeType = (change: string): number => {
  const lowerChange = change.toLowerCase();
  if (lowerChange.includes('new') || lowerChange.includes('added')) {
    return 1; // Star/Feature - highest priority
  }
  if (
    lowerChange.includes('bug fix') ||
    lowerChange.includes('fixed') ||
    lowerChange.includes('resolve') ||
    lowerChange.includes('invalid')
  ) {
    return 3; // Bug fix - lowest priority
  }
  return 2; // Other/Release - middle priority
};

const getChangeIcon = (change: string) => {
  const lowerChange = change.toLowerCase();
  if (
    lowerChange.includes('bug fix') ||
    lowerChange.includes('fixed') ||
    lowerChange.includes('resolve')
  ) {
    return <BugReportIcon sx={{ fontSize: 16, color: orange[600] }} />;
  }
  if (lowerChange.includes('new') || lowerChange.includes('added')) {
    return <StarIcon sx={{ fontSize: 16, color: green[600] }} />;
  }
  return <NewReleasesIcon sx={{ fontSize: 16, color: blue[600] }} />;
};

const parseVersion = (version: string): [number, number, number] => {
  const match = version.replace(/^v/, '').split('.').map(Number);
  return [match[0] || 0, match[1] || 0, match[2] || 0];
};

const getVersionBumpType = (version: string, index: number): 'major' | 'minor' | 'patch' => {
  if (index >= releaseNotes.length - 1) return 'patch'; // First release, default to patch
  const [curMajor, curMinor] = parseVersion(version);
  const [prevMajor, prevMinor] = parseVersion(releaseNotes[index + 1].version);
  if (curMajor > prevMajor) return 'major';
  if (curMinor > prevMinor) return 'minor';
  return 'patch';
};

const getVersionChipColor = (bumpType: 'major' | 'minor' | 'patch') => {
  if (bumpType === 'major') return 'warning';
  if (bumpType === 'minor') return 'success';
  return 'primary';
};

const ReleaseNotes = () => {
  const navigate = useNavigate();

  // Track which versions are open. Default: the latest release expanded, the rest collapsed.
  const [expandedVersions, setExpandedVersions] = React.useState<Set<string>>(
    () => new Set(releaseNotes.length > 0 ? [releaseNotes[0].version] : []),
  );

  const toggleVersion = (version: string) => {
    setExpandedVersions((prev) => {
      const next = new Set(prev);
      if (next.has(version)) next.delete(version);
      else next.add(version);
      return next;
    });
  };

  const expandAll = () => setExpandedVersions(new Set(releaseNotes.map((r) => r.version)));
  const collapseAll = () => setExpandedVersions(new Set());

  return (
    <Container
      maxWidth="md"
      sx={{
        py: 4,
        '@media print': {
          maxWidth: 'none',
          py: 0,
        },
      }}
    >
      <Box
        sx={{
          p: 3,
          '@media print': {
            p: 1,
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            mb: 3,
            '@media print': {
              mb: 2,
            },
          }}
        >
          <IconButton
            onClick={() => navigate(-1)}
            sx={{
              mr: 2,
              '@media print': {
                display: 'none',
              },
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography
            variant="h4"
            component="h1"
            sx={{
              fontWeight: 'bold',
              flexGrow: 1,
              '@media print': {
                fontSize: '1.5rem',
                mb: 1,
              },
            }}
          >
            Version History
          </Typography>
          {releaseNotes.length > 1 && (
            <Stack direction="row" spacing={1} sx={{ '@media print': { display: 'none' } }}>
              <Button size="small" onClick={expandAll}>
                Expand all
              </Button>
              <Button size="small" onClick={collapseAll}>
                Collapse all
              </Button>
            </Stack>
          )}
        </Box>

        <Stack spacing={1.5}>
          {releaseNotes.map((release, index) => {
            const isExpanded = expandedVersions.has(release.version);
            return (
              <Accordion
                key={release.version}
                expanded={isExpanded}
                onChange={() => toggleVersion(release.version)}
                disableGutters
                elevation={1}
                // square={true} disables MUI's default behavior of treating stacked
                // accordions as one visual group (which strips corner radii from
                // non-first / non-last items, producing the flat-top-on-first-card
                // / flat-bottom-on-last-card look). Each release reads as its own
                // standalone card here, so we force all four corners rounded
                // uniformly via the borderRadius below.
                square
                sx={{
                  borderRadius: 3,
                  border: '1px solid',
                  borderColor: grey[200],
                  // MUI inserts a divider as a ::before pseudo by default; remove it so adjacent
                  // accordions don't render a double border.
                  '&:before': { display: 'none' },
                  '&.Mui-expanded': {
                    margin: 0,
                    borderColor: blue[200],
                  },
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    borderColor: blue[200],
                  },
                  '@media print': {
                    boxShadow: 'none',
                    border: '1px solid #e0e0e0',
                  },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    px: 3,
                    py: 1,
                    '& .MuiAccordionSummary-content': {
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      m: 0,
                    },
                    '& .MuiAccordionSummary-content.Mui-expanded': {
                      m: 0,
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <Chip
                      label={release.version}
                      color={getVersionChipColor(getVersionBumpType(release.version, index)) as any}
                      sx={{
                        fontWeight: 'bold',
                        fontSize: '0.875rem',
                      }}
                    />
                    {release.title && (
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          color: grey[800],
                          fontSize: '1.1rem',
                        }}
                      >
                        {release.title}
                      </Typography>
                    )}
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{
                      color: grey[600],
                      fontSize: '0.875rem',
                      fontWeight: 500,
                      mr: 2,
                    }}
                  >
                    {release.date}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 3, pb: 3, pt: 0 }}>
                  <Stack spacing={1.5}>
                    {[...release.changes]
                      .sort((a, b) => getChangeType(a) - getChangeType(b))
                      .map((change, changeIndex) => (
                        <Box
                          key={changeIndex}
                          sx={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: 1.5,
                            py: 0.5,
                          }}
                        >
                          <Box sx={{ mt: 0.25 }}>{getChangeIcon(change)}</Box>
                          <Typography
                            variant="body2"
                            sx={{
                              color: grey[700],
                              fontSize: '0.9rem',
                              lineHeight: 1.5,
                              flex: 1,
                            }}
                          >
                            {change}
                          </Typography>
                        </Box>
                      ))}
                  </Stack>
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Stack>

        {releaseNotes.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="body1" color="text.secondary">
              No release notes available yet
            </Typography>
          </Box>
        )}
      </Box>

      {/* For print: every accordion expands so the printed page has full content. */}
      <style>{`
        @media print {
          .MuiAccordion-root .MuiCollapse-root {
            height: auto !important;
            visibility: visible !important;
          }
          .MuiAccordion-root .MuiCollapse-wrapper {
            height: auto !important;
          }
          .MuiAccordionSummary-expandIconWrapper {
            display: none !important;
          }
        }
      `}</style>
    </Container>
  );
};

export default ReleaseNotes;
